import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FaMagic, FaRibbon, FaStar } from "react-icons/fa";
import { getCandyIdByName, getCandyConfig } from "../../../data/candyConfig";
import { getCandyBaseLogo, getCandyLogo } from "../../../data/candyLogos";
import { getAnimationIntensity } from "./index";
import { getPhasesForCandy } from "./soundRegistry";
import useAnimationSound from "../../../hooks/useAnimationSound";
import "./CandyWrapAnimation.css";

const CALL_OUTS = ["WRAP IT", "SEALED", "TIDY", "SHEESH"];

const PHASE_LABELS = {
  snatching: "Snatching",
  cranking: "Cranking",
  stamping: "Stamping",
  bursting: "Bursting",
  victory: "Victory",
  complete: "Complete",
};

const CandyWrapAnimation = ({
  pair,
  amount = 1,
  onComplete,
  onPhaseChange,
}) => {
  const [phase, setPhase] = useState("snatching");
  const prefersReducedMotion = useReducedMotion();

  const intensity = getAnimationIntensity(pair?.version);
  const { playPhaseSound, preloadSounds } = useAnimationSound(
    "CandyWrap",
    intensity,
    true
  );

  const playPhaseSoundRef = useRef(playPhaseSound);
  const onCompleteRef = useRef(onComplete);
  const onPhaseChangeRef = useRef(onPhaseChange);

  playPhaseSoundRef.current = playPhaseSound;
  onCompleteRef.current = onComplete;
  onPhaseChangeRef.current = onPhaseChange;

  const candyId = useMemo(() => getCandyIdByName(pair?.family), [pair?.family]);
  const candyConfig = useMemo(() => getCandyConfig(candyId), [candyId]);
  const candyLogo = useMemo(() => {
    if (!candyId) {
      return null;
    }

    return getCandyLogo(candyId, pair?.version) || getCandyBaseLogo(candyId);
  }, [candyId, pair?.version]);

  const confetti = useMemo(() => {
    const count = intensity >= 5 ? 18 : intensity >= 3 ? 14 : 10;

    return Array.from({ length: count }, (_, index) => ({
      id: index,
      angle: -88 + (176 / Math.max(count - 1, 1)) * index,
      distance: 90 + ((index * 17) % 72),
      delay: index * 0.02,
      rotate: -42 + ((index * 23) % 84),
      size: 10 + (index % 4) * 4,
      duration: 0.56 + (index % 3) * 0.08,
    }));
  }, [intensity]);

  const sparkles = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => ({
        id: index,
        x: -150 + index * 52,
        y: -110 + (index % 2) * 34,
        delay: index * 0.05,
        size: 12 + (index % 3) * 5,
      })),
    []
  );

  const wrapText = useMemo(() => {
    const seed = String(pair?.pairSlug || pair?.displayName || pair?.beforeLabel || "wrap")
      .split("")
      .reduce((total, char) => total + char.charCodeAt(0), 0);

    return CALL_OUTS[seed % CALL_OUTS.length];
  }, [pair?.beforeLabel, pair?.displayName, pair?.pairSlug]);

  const accent = pair?.accent || "#d94f70";
  const glow = pair?.glow || "rgba(217, 79, 112, 0.35)";
  const gradient =
    pair?.versionGradient ||
    "linear-gradient(135deg, rgba(217,79,112,0.9), rgba(245,188,75,0.95))";

  useEffect(() => {
    preloadSounds(getPhasesForCandy("CandyWrap"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const signalPhase = (nextPhase) => {
      setPhase(nextPhase);
      onPhaseChangeRef.current?.(nextPhase);
    };

    const timings = prefersReducedMotion
      ? {
          cranking: 220,
          stamping: 540,
          bursting: 820,
          victory: 1060,
          complete: 1320,
        }
      : {
          cranking: 420,
          stamping: 980,
          bursting: 1460,
          victory: 2140,
          complete: 2960,
        };

    const timers = [];

    signalPhase("snatching");
    playPhaseSoundRef.current("snatching");

    timers.push(
      setTimeout(() => {
        signalPhase("cranking");
        playPhaseSoundRef.current("cranking");
      }, timings.cranking)
    );

    timers.push(
      setTimeout(() => {
        signalPhase("stamping");
        playPhaseSoundRef.current("stamping");
      }, timings.stamping)
    );

    timers.push(
      setTimeout(() => {
        signalPhase("bursting");
        playPhaseSoundRef.current("bursting");
      }, timings.bursting)
    );

    timers.push(
      setTimeout(() => {
        signalPhase("victory");
        playPhaseSoundRef.current("victory");
      }, timings.victory)
    );

    timers.push(
      setTimeout(() => {
        signalPhase("complete");
        onCompleteRef.current?.();
      }, timings.complete)
    );

    return () => timers.forEach(clearTimeout);
  }, [prefersReducedMotion]);

  return (
    <div
      className={`candy-wrap-animation phase-${phase}`}
      style={{
        "--wrap-accent": accent,
        "--wrap-glow": glow,
        "--wrap-gradient": gradient,
      }}
    >
      <div className="candy-wrap-stage">
        <div className="candy-wrap-spotlight candy-wrap-spotlight-a" />
        <div className="candy-wrap-spotlight candy-wrap-spotlight-b" />
        <motion.div
          className="candy-wrap-floor-shadow"
          animate={{
            scaleX: phase === "bursting" || phase === "victory" ? 1.12 : 0.92,
            opacity: phase === "stamping" ? 0.36 : 0.24,
          }}
          transition={{ duration: 0.34 }}
        />

        <motion.div
          className="candy-wrap-aura candy-wrap-aura-a"
          animate={{
            opacity: phase === "victory" || phase === "complete" ? 0.92 : 0.48,
            scale: phase === "victory" || phase === "complete" ? 1.16 : 0.92,
          }}
          transition={{ duration: 0.36 }}
        />
        <motion.div
          className="candy-wrap-aura candy-wrap-aura-b"
          animate={{
            opacity: phase === "bursting" || phase === "victory" ? 0.62 : 0.22,
            scale: phase === "bursting" || phase === "victory" ? 1.06 : 0.86,
          }}
          transition={{ duration: 0.32 }}
        />

        <AnimatePresence>
          {phase !== "complete" && (
            <motion.div
              className="candy-wrap-phase-badge"
              initial={{ opacity: 0, y: 6 }}
              animate={{
                y: phase === "bursting" || phase === "victory" ? -2 : 0,
                opacity: 1,
              }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22 }}
            >
              <FaMagic />
              <span>{PHASE_LABELS[phase] || "Wrapping"}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="candy-wrap-conveyor">
          <div className="candy-wrap-conveyor-track" />
          <motion.div
            className="candy-wrap-conveyor-glow"
            animate={{
              x:
                phase === "snatching"
                  ? [-88, -18]
                  : phase === "cranking"
                    ? [-18, 32]
                    : phase === "stamping"
                      ? [32, 48]
                      : 48,
              opacity:
                phase === "bursting" || phase === "victory" || phase === "complete"
                  ? 0
                  : 0.9,
            }}
            transition={{ duration: 0.42, ease: "easeInOut" }}
          />
        </div>

        <div className="candy-wrap-machine">
          <motion.div
            className="candy-wrap-machine-spinner"
            animate={{
              rotate:
                phase === "cranking"
                  ? 360
                  : phase === "stamping"
                    ? 520
                    : phase === "bursting" || phase === "victory"
                      ? 760
                      : 0,
              scale:
                phase === "bursting" || phase === "victory"
                  ? [1, 1.08, 1]
                  : 1,
            }}
            transition={{
              rotate: {
                duration:
                  phase === "bursting" || phase === "victory"
                    ? 0.8
                    : phase === "stamping"
                      ? 0.34
                      : 0.62,
                ease: "easeInOut",
              },
              scale: { duration: 0.42, ease: "easeOut" },
            }}
          />
          <div className="candy-wrap-machine-core">
            <FaRibbon />
          </div>
          <motion.div
            className="candy-wrap-beam"
            animate={{
              opacity:
                phase === "cranking" || phase === "stamping"
                  ? [0.24, 0.9, 0.28]
                  : phase === "bursting"
                    ? 0.96
                    : 0.18,
            }}
            transition={{ duration: 0.42, repeat: phase === "cranking" ? Infinity : 0 }}
          />
        </div>

        <motion.div
          className="candy-wrap-token token-before"
          initial={{ x: -176, opacity: 0, rotate: -16, scale: 0.82 }}
          animate={
            phase === "snatching"
              ? { x: -56, opacity: 1, rotate: 8, scale: 1 }
              : phase === "cranking"
                ? {
                    x: -18,
                    opacity: 1,
                    rotate: [4, -4, 4],
                    scale: [1, 1.05, 0.98],
                    y: [0, -2, 0],
                  }
                : phase === "stamping"
                  ? {
                      x: 2,
                      opacity: [1, 1, 0.2],
                      rotate: [0, 12, -8],
                      scaleX: [1, 1.22, 0.86],
                      scaleY: [1, 0.78, 1.1],
                    }
                  : { x: 8, opacity: 0, rotate: -14, scale: 0.74 }
          }
          transition={{
            duration: phase === "snatching" ? 0.42 : 0.38,
            ease: "easeOut",
          }}
        >
          <div className="candy-wrap-token-shell">
            <div className="candy-wrap-token-count">x{amount}</div>
            <div className="candy-wrap-token-art">
              {candyLogo ? (
                <img src={candyLogo} alt={pair?.beforeLabel || "Loose candy"} />
              ) : (
                <span>{candyConfig?.icon || "C"}</span>
              )}
            </div>
            <strong>{pair?.beforeLabel || "Loose Candy"}</strong>
          </div>
        </motion.div>

        <motion.div
          className="candy-wrap-stamp"
          initial={{ y: -192, opacity: 0, rotate: -10, scale: 0.84 }}
          animate={
            phase === "stamping"
              ? {
                  y: [-192, -22, 8, -10],
                  opacity: 1,
                  rotate: [-10, 0, 2, 0],
                  scale: [0.84, 1.06, 0.92, 1],
                }
              : phase === "bursting" || phase === "victory" || phase === "complete"
                ? { y: -168, opacity: 0, rotate: 8, scale: 0.82 }
                : { y: -192, opacity: 0, rotate: -10, scale: 0.84 }
          }
          transition={{ duration: 0.46, ease: "easeInOut" }}
        >
          <div className="candy-wrap-stamp-face">
            <span>SLAP</span>
            <span>SEAL</span>
          </div>
        </motion.div>

        <AnimatePresence>
          {(phase === "stamping" || phase === "bursting" || phase === "victory") && (
            <motion.div
              className="candy-wrap-shockwave"
              initial={{ opacity: 0.2, scale: 0.2 }}
              animate={{ opacity: [0.28, 0.12, 0], scale: [0.2, 1, 1.18] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.48, ease: "easeOut" }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {(phase === "bursting" || phase === "victory" || phase === "complete") && (
            <>
              <motion.div
                className="candy-wrap-flash"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.95, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />

              <motion.div
                className="candy-wrap-callout"
                initial={{ opacity: 0, y: 16, scale: 0.6, rotate: -8 }}
                animate={{ opacity: 1, y: -4, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, y: -12, scale: 0.8 }}
                transition={{ duration: 0.34, ease: "easeOut" }}
              >
                <FaMagic />
                <span>{wrapText}</span>
              </motion.div>

              <motion.div
                className="candy-wrap-token token-after"
                initial={{ x: 0, opacity: 0, rotate: -18, scale: 0.18 }}
                animate={{
                  x:
                    phase === "bursting"
                      ? [0, 62, 86]
                      : phase === "victory" || phase === "complete"
                        ? 86
                        : 0,
                  y:
                    phase === "victory" || phase === "complete"
                      ? [0, -8, 0]
                      : [0, -18, 0],
                  opacity: [0, 1, 1],
                  rotate: [-18, 10, 2],
                  scale:
                    phase === "bursting"
                      ? [0.18, 1.18, 1]
                      : phase === "victory" || phase === "complete"
                        ? [1, 1.02, 1]
                        : 1,
                }}
                exit={{ opacity: 0, x: 148, scale: 0.72 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <div className="candy-wrap-token-shell wrapped">
                  <div className="candy-wrap-token-ribbon">
                    <FaRibbon />
                  </div>
                  <div className="candy-wrap-token-art">
                    {candyLogo ? (
                      <img src={candyLogo} alt={pair?.afterLabel || "Wrapped candy"} />
                    ) : (
                      <span>{candyConfig?.icon || "C"}</span>
                    )}
                  </div>
                  <strong>{pair?.afterLabel || "Candy Wrap"}</strong>
                </div>
              </motion.div>

              <motion.div
                className="candy-wrap-ribbon-tail ribbon-left"
                initial={{ opacity: 0, x: 46, y: -6, rotate: 24, scale: 0.8 }}
                animate={{ opacity: 1, x: 34, y: 10, rotate: -16, scale: 1 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.46, ease: "easeOut" }}
              />
              <motion.div
                className="candy-wrap-ribbon-tail ribbon-right"
                initial={{ opacity: 0, x: 148, y: -2, rotate: -26, scale: 0.8 }}
                animate={{ opacity: 1, x: 166, y: 12, rotate: 16, scale: 1 }}
                exit={{ opacity: 0, x: 220 }}
                transition={{ duration: 0.46, ease: "easeOut" }}
              />

              <div className="candy-wrap-confetti-cloud">
                {confetti.map((piece) => (
                  <motion.span
                    key={piece.id}
                    className="candy-wrap-confetti"
                    style={{
                      width: `${piece.size}px`,
                      height: `${Math.max(piece.size * 0.42, 6)}px`,
                    }}
                    initial={{ opacity: 0, x: 0, y: 0, rotate: piece.rotate, scale: 0.4 }}
                    animate={{
                      opacity: [0, 1, 0],
                      x: Math.cos((piece.angle * Math.PI) / 180) * piece.distance,
                      y: Math.sin((piece.angle * Math.PI) / 180) * piece.distance * 0.72,
                      rotate: piece.rotate + 140,
                      scale: [0.4, 1, 0.8],
                    }}
                    transition={{
                      delay: piece.delay,
                      duration: piece.duration,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>

              <div className="candy-wrap-sparkles">
                {sparkles.map((sparkle) => (
                  <motion.span
                    key={sparkle.id}
                    className="candy-wrap-sparkle"
                    style={{
                      left: `calc(50% + ${sparkle.x}px)`,
                      top: `calc(50% + ${sparkle.y}px)`,
                      width: `${sparkle.size}px`,
                      height: `${sparkle.size}px`,
                    }}
                    initial={{ opacity: 0, scale: 0.2, rotate: 0 }}
                    animate={{ opacity: [0, 1, 0], scale: [0.2, 1, 0.2], rotate: 90 }}
                    transition={{
                      delay: sparkle.delay,
                      duration: 0.52,
                      ease: "easeOut",
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </AnimatePresence>

        <div className="candy-wrap-footer">
          <div className="candy-wrap-footer-pill">
            <FaStar />
            <span>{pair?.displayName || "Candy Wrapper"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandyWrapAnimation;
